import type { FC } from 'react';
import { useRef } from 'react';
import type { NumberFormatValues } from 'react-number-format';
import { useTranslation } from 'next-i18next';
import { loadCouponPreviewV2Request, selectCouponAmountErrorTypeV2, selectCouponBetAmountV2, selectCouponLimitsV2, selectCouponPreviewLoaded, selectCouponSelectedBetsV2, selectCurrentPaymentSourceV2, selectGetCouponPreviewDefaultBet, selectIsCouponQuickBetsV2, selectUserCurrency, setCouponBetAmountV2, useLocalizedNumberFormatters, useQueryPopupOpen } from '@mwl/core-lib';
import { NoSSR } from '@mwl/ui';
import { Button } from '@/components/Button/Button';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { selectIsCouponSumChanged, setIsCouponSumChanged } from '@/store';
import { CouponAccountSelectItem } from '../CouponAccountSelectItem/CouponAccountSelectItem';
import { CouponSumBadges } from '../CouponSumBadges/CouponSumBadges';
import { CouponSumField } from './components/CouponSumField/CouponSumField';
import { CouponSumFieldWrapper } from './components/CouponSumFieldWrapper/CouponSumFieldWrapper';
import { useAmountError, useDebouncedLoadPreview } from './CouponSum.hooks';
import { trimValueToAcceptable } from './CouponSum.utils';
import styles from './CouponSum.module.scss';
const CouponSum: FC = () => {
  const {
    t
  } = useTranslation('sport');
  const dispatch = useTypedDispatch();
  const limits = useTypedSelector(selectCouponLimitsV2);
  const isQuickBets = useTypedSelector(selectIsCouponQuickBetsV2);
  const currentPaymentSource = useTypedSelector(selectCurrentPaymentSourceV2);
  const amountErrorType = useTypedSelector(selectCouponAmountErrorTypeV2);
  const betAmount = useTypedSelector(selectCouponBetAmountV2);
  const getBetDefaultAmount = useTypedSelector(selectGetCouponPreviewDefaultBet);
  const selectedBets = useTypedSelector(selectCouponSelectedBetsV2);
  const isPreviewLoaded = useTypedSelector(selectCouponPreviewLoaded);
  const isCouponSumChanges = useTypedSelector(selectIsCouponSumChanged);
  const currency = useTypedSelector(selectUserCurrency);
  const prevValue = useRef(betAmount);
  const isDataLoaded = !selectedBets.length || selectedBets.length && isPreviewLoaded;
  const amountError = useAmountError();
  const debouncedLoadPreview = useDebouncedLoadPreview();
  const {
    onOpenPopup
  } = useQueryPopupOpen();
  const {
    formatPriceByCurrencyCodeWithLocale
  } = useLocalizedNumberFormatters();
  const setValueToPreview = (newValue: string) => {
    const numberValue = +newValue;
    if (!numberValue) {
      dispatch(setCouponBetAmountV2(prevValue.current));
    } else {
      dispatch(setCouponBetAmountV2(newValue));
      prevValue.current = newValue;
    }
  };
  const handleSumBadgeClick = (sum: string) => {
    if (+sum !== +(prevValue.current || 0)) {
      setValueToPreview(sum);
      if (!isQuickBets) {
        dispatch(loadCouponPreviewV2Request());
      }
    }
  };
  const handleChange = ({
    value: sum
  }: NumberFormatValues) => {
    let innerSum = sum;
    innerSum = trimValueToAcceptable(innerSum, +currentPaymentSource.amount, limits?.max?.amount);
    dispatch(setCouponBetAmountV2(innerSum));
    if (prevValue.current !== innerSum) {
      debouncedLoadPreview();
    }

    // если пользователь не ввел данные то устанавливаем prevValue.current значение из вычесленного defaultAmount
    // чтобы при потере фокуса установить необходимое значение
    const defaultAmount = getBetDefaultAmount(sum)?.defaultAmount;
    if (+innerSum) {
      prevValue.current = innerSum;
    } else if (+(prevValue?.current || 0) <= (defaultAmount ?? 0)) {
      prevValue.current = defaultAmount ? String(defaultAmount) : '0';
    }
  };
  const handleBlur = () => {
    const sum = amountErrorType ? getBetDefaultAmount(betAmount)?.defaultAmount : parseFloat(String(betAmount || 0));
    handleSumBadgeClick(String(sum || ''));
    if (betAmount !== '' && betAmount !== null) {
      dispatch(setIsCouponSumChanged(true));
    }
  };
  const handleFocus = () => {
    if (!isCouponSumChanges) {
      dispatch(setCouponBetAmountV2(''));
    }
  };
  const showDepositPopup = () => {
    onOpenPopup({
      queryName: 'deposit'
    });
  };
  if (!isDataLoaded) {
    return null;
  }
  return <>
      <div className={cn(styles.root, {
      [styles.mobilePreviewError]: isQuickBets && amountErrorType === 'NOT_ENOUGH_BALANCE'
    })}>
        {isQuickBets && !currentPaymentSource.amount ? null : <CouponSumFieldWrapper>
            <CouponSumField onChange={handleChange} onBlur={handleBlur} onFocus={handleFocus} />
          </CouponSumFieldWrapper>}
        {!isQuickBets && <NoSSR>
            <CouponSumBadges onBadgeClick={handleSumBadgeClick} />{' '}
          </NoSSR>}
        {!!amountError && <p className={cn(styles.infoWrapper, styles.error)}>{t(amountError?.[0], amountError?.[1])}</p>}
      </div>
      {isQuickBets && amountErrorType === 'NOT_ENOUGH_BALANCE' && <>
          <CouponAccountSelectItem active={false} id={currentPaymentSource.id} type={currentPaymentSource.type} text={t('coupon.select.real_balance', 'Your balance')} amount={formatPriceByCurrencyCodeWithLocale(+currentPaymentSource.amount, currency)} className={styles.balance} />
          <Button as="button" onClick={showDepositPopup} fullWidth variant="filled" color="green" size="lg" fontWeight="bold" align="center" fontSize="lg" rounded>
            {t('coupon.account_deposit', 'Deposit')}
          </Button>
        </>}
    </>;
};
export { CouponSum };